import { useState } from 'react';
import { FORM_ERROR } from 'final-form';
import cookie from 'js-cookie';
import { t } from 'ttag';

import { registration } from '@urls';
import { COLLECTING_EMAILS_COOKIE } from '@constants';
import { notifyError, notifySuccess } from '@utils/notifications';
import {
  getEmailNotAuthUser,
  getFieldsSingleError,
  setEmailNotAuthUser,
} from '@utils/common';
import { useIsAuth } from '@hook/useIsAuth';
import { Product, ProductFavoriteType, useProductFavorite } from '@hook/useProductFavorite';
import { CollectingEmailsFormContainer } from '@containers/CollectingEmailsForm';
import { ProductFavoritesToggle } from '@components/ProductFavoritesToggle';
import { CollectingEmailsForm, FormData } from '@components/CollectingEmailsForm';

import { useSubscribeToProductMutation } from './gql/graphql.v4.generated';

interface Props {
  className?: string,
  product: Product,
  type: ProductFavoriteType,
  isDetailed?: boolean,
  onChange?: (isLiked: boolean) => void,
}

export const ProductFavoritesToggleContainer: React.FC<Props> = ({
  className,
  product,
  type,
  isDetailed,
  onChange,
}) => {
  const isAuth = useIsAuth();
  const {
    handleAddToFavorites,
    handleRemoveFromFavorites,
  } = useProductFavorite({
    type,
    product,
  });

  const [isSubscribeToProductFormOpen, setIsSubscribeToProductFormOpen] = useState(false);
  const [isCollectEmailFormOpen, setIsCollectEmailFormOpen] = useState(false);

  const [subscribeToProduct] = useSubscribeToProductMutation({
    context: { v4: true },
  });

  const handleSubscribeToProduct = async (email: string) => {
    const resp = await subscribeToProduct({
      variables: {
        productId: product.id,
        email,
      },
    });

    if (resp.data?.subscribeToProduct.errors?.length) {
      const formError = getFieldsSingleError(resp.data?.subscribeToProduct.errors);

      notifyError(
        formError.__all__,
      );
    } else {
      notifySuccess(t`Ви успішно підписалися на товар`);
    }
  };

  const redirectToLogin = () => {
    document.location.href = registration;
  };

  const handleSubscribeToProductFormSubmit = async (formData: FormData) => {
    const { email } = formData;

    setEmailNotAuthUser(email);

    const resp = await subscribeToProduct({
      variables: {
        productId: product.id,
        email,
      },
    });
    if (resp.data?.subscribeToProduct.errors?.length) {
      const formError = getFieldsSingleError(resp.data?.subscribeToProduct.errors);

      return {
        email: formError.email,
        [FORM_ERROR]: formError.__all__,
      };
    }

    notifySuccess(t`Ви успішно підписалися на товар`);
    setIsSubscribeToProductFormOpen(false);
    setTimeout(redirectToLogin, 1500);
    return undefined;
  };

  const handleToggleFavorite = async (isLiked: boolean) => {
    if (!isAuth) {
      const email = getEmailNotAuthUser();

      if (email) {
        await handleSubscribeToProduct(email);
      } else {
        setIsSubscribeToProductFormOpen(true);
      }
    } else {
      if (!cookie.get(COLLECTING_EMAILS_COOKIE)) {
        setIsCollectEmailFormOpen(true);
      }

      if (product.isLiked) {
        await handleRemoveFromFavorites();
      } else {
        await handleAddToFavorites();
      }
    }

    if (onChange) {
      onChange(isLiked);
    }
  };

  const handleCloseSubscribeToProductForm = () => {
    setIsSubscribeToProductFormOpen(false);
    redirectToLogin();
  };

  return (
    <>
      <ProductFavoritesToggle
        className={className}
        likes={product.likes}
        isLiked={product.isLiked}
        onToggle={handleToggleFavorite}
        isDetailed={isDetailed}
      />

      {/* if user not auth then subscribe to product else update email in profile */}
      {isAuth ? (
        <CollectingEmailsFormContainer
          isOpen={isCollectEmailFormOpen}
          onRequestClose={() => setIsCollectEmailFormOpen(false)}
        />
      ) : (
        <CollectingEmailsForm
          isOpen={isSubscribeToProductFormOpen}
          onSubmit={handleSubscribeToProductFormSubmit}
          onRequestClose={handleCloseSubscribeToProductForm}
        />
      )}
    </>
  );
};
